import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";

interface EditCouncilProps {}

interface CouncilFormData {
  councilName: string;
  councilType: string;
  field: string;
  managingUnit: string;
  decisionNumber: string;
  decisionDate: string;
  attachedFiles: File[];
}

const EditCouncil: React.FC<EditCouncilProps> = () => {
  const navigate = useNavigate();

  // Get ID from URL path
  const getCouncilId = () => {
    const pathParts = window.location.pathname.split("/");
    return pathParts[pathParts.length - 1];
  };

  const id = getCouncilId();

  const [formData, setFormData] = useState<CouncilFormData>({
    councilName: "",
    councilType: "",
    field: "",
    managingUnit: "",
    decisionNumber: "",
    decisionDate: "",
    attachedFiles: [],
  });

  // Mock data for editing - in real app, this would come from API
  const mockCouncilData: CouncilFormData = {
    councilName: "Hội đồng xét duyệt đề tài số 1 năm 2025",
    councilType: "Hội đồng xét duyệt thuyết minh đề cương",
    field: "Công nghệ thông tin và tự động hóa",
    managingUnit: "Phòng Khoa học",
    decisionNumber: "01",
    decisionDate: "2025-07-20",
    attachedFiles: [],
  };

  useEffect(() => {
    // Load council data for editing
    if (id) {
      // In real app, fetch data from API based on id
      setFormData(mockCouncilData);
    }
  }, [id]);

  const handleInputChange = (
    field: keyof CouncilFormData,
    value: string | File | File[] | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value as never,
    }));
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      handleInputChange("attachedFiles", [
        ...formData.attachedFiles,
        ...newFiles,
      ]);
    }
  };

  const handleSave = () => {
    console.log("Updating council data:", formData);
    // TODO: Implement update functionality
    navigate("/councils");
  };

  const handleCancel = () => {
    window.history.back();
  };

  return (
    <PageShell title="Sửa Hội đồng">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">Sửa Hội đồng</h1>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-lg p-6">
          <div className="space-y-8">
            {/* Row 1: Tên hội đồng - Loại hội đồng (READ-ONLY) - Lĩnh vực đánh giá */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên hội đồng <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="text"
                  value={formData.councilName}
                  onChange={(e) =>
                    handleInputChange("councilName", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập tên hội đồng..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loại hội đồng
                </label>
                {/* READ-ONLY field - cannot be edited */}
                <input
                  type="text"
                  value={formData.councilType}
                  readOnly
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                  placeholder="Loại hội đồng (không thể chỉnh sửa)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Trường này không thể chỉnh sửa
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lĩnh vực đánh giá
                </label>
                <select
                  value={formData.field}
                  onChange={(e) => handleInputChange("field", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn lĩnh vực đánh giá -</option>
                  <option>Công nghệ thông tin và tự động hóa</option>
                  <option>Khoa học tự nhiên</option>
                  <option>Khoa học xã hội</option>
                </select>
              </div>
            </div>

            {/* Row 2: Đơn vị chủ trì - Số quyết định - Ngày quyết định */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Đơn vị chủ trì
                </label>
                <input
                  type="text"
                  value={formData.managingUnit}
                  onChange={(e) =>
                    handleInputChange("managingUnit", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Đơn vị chủ trì..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số quyết định
                </label>
                <input
                  type="text"
                  value={formData.decisionNumber}
                  onChange={(e) =>
                    handleInputChange("decisionNumber", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Số quyết định..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ngày quyết định
                </label>
                <input
                  type="date"
                  value={formData.decisionDate}
                  onChange={(e) =>
                    handleInputChange("decisionDate", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* File đính kèm */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File đính kèm
                </label>

                {/* Custom File Input */}
                <div className="flex items-center space-x-4">
                  <input
                    id="council-file-upload"
                    type="file"
                    multiple
                    onChange={handleAttachmentChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="council-file-upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    Choose Files
                  </label>
                  <span className="text-sm text-gray-500">
                    {formData.attachedFiles.length === 0
                      ? "No file chosen"
                      : `${formData.attachedFiles.length} file(s) chosen`}
                  </span>
                </div>

                {/* File List */}
                {formData.attachedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Files:
                    </h4>
                    <div
                      className={`space-y-2 ${
                        formData.attachedFiles.length > 3
                          ? "max-h-48 overflow-y-auto pr-2"
                          : ""
                      }`}
                    >
                      {formData.attachedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <svg
                              className="w-4 h-4 text-gray-400 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const updatedFiles =
                                formData.attachedFiles.filter(
                                  (_, i) => i !== index
                                );
                              handleInputChange("attachedFiles", updatedFiles);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors flex-shrink-0"
                            title="Xóa file"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            <span>Hủy</span>
          </button>
          <button
            onClick={handleSave}
            className="bg-green-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <span>Lưu dữ liệu</span>
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default EditCouncil;
